/**
 * D4 (20435000003242): the accommodation basis for a deposit on a day is the
 * agreement version in force on that day. A dated payment change
 * (`record_accommodation_payment_change_controlled`) supersedes the version, so
 * days before the change keep the old payment type and daily payment.
 *
 * Mirrors `internal.accommodation_daily_amount_for_day(tenant, deposit, day)`:
 * - versions with status ACTIVE, SUPERSEDED or ENDED whose window covers the
 *   day; latest effectiveFrom, then latest createdAt, then highest id wins;
 * - no version: the deposit's own payment type and daily rate (rows written
 *   before agreements were versioned);
 * - amount is the daily rate for DAP and COMBINATION, otherwise 0.
 *
 * Money is integer cents. Indexation (D5) is not applied here yet.
 */

export type AccommodationBasisSource = 'AGREEMENT_VERSION' | 'DEPOSIT_PROJECTION';

export interface AccommodationDatedVersion {
  id: string;
  status: string;
  /** YYYY-MM-DD */
  effectiveFrom: string;
  /** YYYY-MM-DD or null (open). */
  effectiveTo: string | null;
  createdAt?: string | null;
  paymentType: string;
  dapDailyCents: number;
}

export interface AccommodationDepositBasis {
  paymentType: string;
  dapDailyCents: number;
}

export interface AccommodationDailyAmountForDay {
  day: string;
  agreementId: string | null;
  source: AccommodationBasisSource;
  paymentType: string;
  rateCents: number;
  amountCents: number;
  component: 'DAP';
}

const IN_FORCE_STATUSES = new Set(['ACTIVE', 'SUPERSEDED', 'ENDED']);

function dayKey(value: string): string {
  const day = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) {
    throw new RangeError(`accommodation_basis_day_invalid:${value}`);
  }
  return day;
}

/** The agreement version in force on `day`, or null. */
export function accommodationVersionInForce<T extends AccommodationDatedVersion>(
  versions: readonly T[],
  day: string,
): T | null {
  const key = dayKey(day);
  let best: T | null = null;
  for (const version of versions) {
    if (!IN_FORCE_STATUSES.has(version.status)) continue;
    const from = dayKey(version.effectiveFrom);
    const to = version.effectiveTo ? dayKey(version.effectiveTo) : null;
    if (from > key || (to !== null && to < key)) continue;
    if (best === null || compareVersions(version, best) > 0) best = version;
  }
  return best;
}

function compareVersions(a: AccommodationDatedVersion, b: AccommodationDatedVersion): number {
  return (
    dayKey(a.effectiveFrom).localeCompare(dayKey(b.effectiveFrom)) ||
    (a.createdAt ?? '').localeCompare(b.createdAt ?? '') ||
    a.id.localeCompare(b.id)
  );
}

function amountFor(paymentType: string, rateCents: number): number {
  return paymentType === 'DAP' || paymentType === 'COMBINATION' ? rateCents : 0;
}

/** Same result as `internal.accommodation_daily_amount_for_day`. */
export function accommodationDailyAmountForDay(input: {
  day: string;
  agreementVersions: readonly AccommodationDatedVersion[];
  deposit: AccommodationDepositBasis;
}): AccommodationDailyAmountForDay {
  const day = dayKey(input.day);
  const version = accommodationVersionInForce(input.agreementVersions, day);
  if (version) {
    if (!Number.isSafeInteger(version.dapDailyCents)) {
      throw new RangeError(`accommodation_basis_cents_invalid:${version.id}`);
    }
    return {
      day,
      agreementId: version.id,
      source: 'AGREEMENT_VERSION',
      paymentType: version.paymentType,
      rateCents: version.dapDailyCents,
      amountCents: amountFor(version.paymentType, version.dapDailyCents),
      component: 'DAP',
    };
  }
  if (!Number.isSafeInteger(input.deposit.dapDailyCents)) {
    throw new RangeError('accommodation_basis_cents_invalid:deposit');
  }
  return {
    day,
    agreementId: null,
    source: 'DEPOSIT_PROJECTION',
    paymentType: input.deposit.paymentType,
    rateCents: input.deposit.dapDailyCents,
    amountCents: amountFor(input.deposit.paymentType, input.deposit.dapDailyCents),
    component: 'DAP',
  };
}

/**
 * r 296-5(3) for a payment change: (agreed price − refundable amount) × MPIR
 * fixed on the price agreement day ÷ 365, one half-up round to cents. Same
 * arithmetic as `record_accommodation_payment_change_controlled`. `mpirPct`
 * is a percentage string with at most 6 decimal places (e.g. "7.78").
 */
export function accommodationChangeDapDailyCents(input: {
  agreedRoomPriceCents: number;
  refundableCents: number;
  mpirPct: string | number;
}): number {
  const { agreedRoomPriceCents, refundableCents } = input;
  if (!Number.isSafeInteger(agreedRoomPriceCents) || !Number.isSafeInteger(refundableCents)) {
    throw new RangeError('accommodation_change_cents_invalid');
  }
  const unpaid = BigInt(agreedRoomPriceCents - refundableCents);
  if (unpaid < 0n) throw new RangeError('accommodation_change_refundable_exceeds_price');
  const text = String(input.mpirPct).trim();
  const match = /^(\d+)(?:\.(\d{1,6}))?$/.exec(text);
  if (!match) throw new RangeError(`accommodation_change_mpir_invalid:${text}`);
  // MPIR as an integer scaled by 1e6 percentage points.
  const mpirE6 = BigInt(match[1] ?? '0') * 1_000_000n + BigInt((match[2] ?? '').padEnd(6, '0'));
  // cents = unpaid × mpirE6 / (100 × 1e6 × 365), half-up.
  const numerator = unpaid * mpirE6;
  const denominator = 100n * 1_000_000n * 365n;
  return Number((numerator * 2n + denominator) / (denominator * 2n));
}
